/**
 * Single Soren brief card — primary deep-link + Done / Remind / Don't show again.
 * Money cards: grouped headline + per–Pay App rows (row actions do not dismiss).
 * P2 cadence/retainage cards use WHAT / WHY / DO labels.
 */
import { formatMoney } from '../../lib/formatters'
import type { SorenCard } from './rankSorenCards'

function MoneyBits({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\$[\d,]+\.\d{2})/g).map((part, i) =>
        /^\$[\d,]+\.\d{2}$/.test(part) ? (
          <span key={i} className="soren-money">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

export default function SorenCardView({
  card,
  onOpen,
  onDone,
  onRemind,
  onHide,
}: {
  card: SorenCard
  onOpen: (href: string) => void
  onDone: () => void
  onRemind: () => void
  onHide: () => void
}) {
  const wwd = Boolean(card.what || card.why || (card.doActions && card.doActions.length))
  const moneyRows = card.moneyRows && card.moneyRows.length > 0 ? card.moneyRows : null

  return (
    <article
      className={`soren-task ${card.kind}`}
      data-testid="soren-card"
      data-kind={card.kind}
      data-card-id={card.id}
    >
      {wwd ? (
        <div className="soren-wwd" data-testid="soren-card-wwd">
          {card.what ? (
            <>
              <h4>WHAT</h4>
              <p data-testid="soren-card-what">
                <MoneyBits text={card.what} />
              </p>
            </>
          ) : null}
          {card.why ? (
            <>
              <h4>WHY</h4>
              <p data-testid="soren-card-why">
                <MoneyBits text={card.why} />
              </p>
            </>
          ) : null}
          {(card.doActions?.length || card.primary) && (
            <>
              <h4>DO</h4>
              <p data-testid="soren-card-do" className="soren-wwd-do">
                {(card.doActions && card.doActions.length > 0
                  ? card.doActions
                  : [card.primary, card.secondary].filter(Boolean) as { label: string; href: string }[]
                ).map((d) =>
                  d.href ? (
                    <button
                      key={d.label}
                      type="button"
                      className="soren-action primary"
                      data-testid="soren-card-primary"
                      onClick={() => onOpen(d.href)}
                    >
                      {d.label}
                    </button>
                  ) : d.label === 'Remind me' ? (
                    <button
                      key={d.label}
                      type="button"
                      className="soren-action snooze"
                      data-testid="soren-card-remind"
                      onClick={onRemind}
                    >
                      Remind me
                    </button>
                  ) : d.label === 'Skip this week' ? (
                    <button
                      key={d.label}
                      type="button"
                      className="soren-action snooze"
                      data-testid="soren-card-skip-friday"
                      onClick={onDone}
                    >
                      Skip this week
                    </button>
                  ) : null,
                )}
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          <h3>
            <MoneyBits text={card.title} />
          </h3>
          {moneyRows ? (
            <ul className="soren-money-rows" data-testid="soren-money-rows">
              {moneyRows.map((row) => (
                <li
                  key={row.payAppId}
                  className="soren-money-row"
                  data-testid="soren-money-row"
                  data-pay-app-id={row.payAppId}
                  data-project-id={row.projectId}
                >
                  <div className="soren-money-row-meta">
                    <span className="soren-money-row-project">{row.projectName}</span>
                    <span className="soren-money-row-amt soren-money">
                      {formatMoney(row.amount)}
                    </span>
                    <span className="soren-money-row-days">
                      {row.daysOverdue}d overdue
                    </span>
                  </div>
                  <div className="soren-actions soren-money-row-actions">
                    <button
                      type="button"
                      className="soren-action primary"
                      data-testid="soren-money-row-open"
                      data-href={row.openHref}
                      onClick={() => onOpen(row.openHref)}
                    >
                      Open payments
                    </button>
                    <button
                      type="button"
                      className="soren-action"
                      data-testid="soren-money-row-record"
                      data-href={row.recordHref}
                      onClick={() => onOpen(row.recordHref)}
                    >
                      Record a payment
                    </button>
                  </div>
                </li>
              ))}
              {card.moneyOverflow && card.moneyOverflow.count > 0 ? (
                <li className="soren-money-overflow">
                  <button
                    type="button"
                    className="soren-action hide"
                    data-testid="soren-money-overflow"
                    data-href={card.moneyOverflow.href}
                    onClick={() => onOpen(card.moneyOverflow!.href)}
                  >
                    and {card.moneyOverflow.count} more
                  </button>
                </li>
              ) : null}
            </ul>
          ) : (
            <>
              {card.body ? <p>{card.body}</p> : null}
              <div className="soren-actions">
                <button
                  type="button"
                  className="soren-action primary"
                  data-testid="soren-card-primary"
                  onClick={() => onOpen(card.primary.href)}
                >
                  {card.primary.label}
                </button>
                {card.secondary && (
                  <button
                    type="button"
                    className="soren-action"
                    data-testid="soren-card-secondary"
                    onClick={() => onOpen(card.secondary!.href)}
                  >
                    {card.secondary.label}
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
      <div className="soren-actions soren-lifecycle">
        <button
          type="button"
          className="soren-action done"
          data-testid="soren-card-done"
          onClick={onDone}
        >
          ✓ Done
        </button>
        {!wwd && (
          <button
            type="button"
            className="soren-action snooze"
            data-testid="soren-card-remind"
            onClick={onRemind}
          >
            Remind me
          </button>
        )}
        <button
          type="button"
          className="soren-action hide"
          data-testid="soren-card-hide"
          onClick={onHide}
        >
          Don&apos;t show again
        </button>
      </div>
    </article>
  )
}
